
import React from 'react';
import Hero from '../components/Hero';
import SocialProof from '../components/SocialProof';
import DupesSection from '../components/DupesSection';
import ProductCard from '../components/ProductCard';
import { Product, Dupe } from '../types';
import { Link } from 'react-router-dom';
import { Beaker, Wind, Layers } from 'lucide-react';

interface HomeProps {
  addToCart: (p: Product) => void;
  products: Product[];
  dupes: Dupe[];
}

const Home: React.FC<HomeProps> = ({ addToCart, products, dupes }) => {
  return (
    <div className="overflow-x-hidden bg-brand-offwhite">
      <Hero />
      
      {/* Section Bienvenue */}
      <section className="py-24 bg-brand-cream/30 border-b border-brand-black/5">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <div className="w-24 h-24 mx-auto mb-10 rounded-full overflow-hidden border-2 border-brand-gold/20 shadow-xl bg-brand-offwhite flex items-center justify-center p-2">
            <img 
              src="https://fbaptclwziljpmozftiz.supabase.co/storage/v1/object/public/images/logo.jpeg" 
              alt="Logo R&C" 
              className="w-full h-full object-contain" 
            />
          </div>
          <h2 className="font-serif text-4xl mb-6 italic text-brand-black">Bienvenue chez R & C Essentials</h2>
          <p className="text-brand-black/70 text-lg font-light leading-relaxed">
            Une escale sensorielle proposant les rituels <span className="text-brand-gold font-bold">Bath & Body Works</span>. 
            Importés avec soin des États-Unis pour sublimer votre quotidien au Sénégal.
          </p>
          <div className="w-20 h-[1px] bg-brand-gold/30 mx-auto mt-10"></div>
        </div>
      </section>

      {/* Conseils d'Experte - 3 Colonnes Dark Luxe */}
      <section className="py-24 md:py-32 bg-brand-offwhite">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[10px] uppercase tracking-[0.6em] text-brand-gold mb-4 block font-bold">L'Art de se Parfumer</span>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-black">Conseils d'Experte</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne 1: Chimie */}
            <div className="bg-brand-black text-brand-offwhite p-10 rounded-[2rem] space-y-8 relative overflow-hidden group luxury-transition hover:shadow-2xl">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center text-brand-gold mb-6">
                <Beaker size={24} />
              </div>
              <div>
                <h3 className="font-serif text-3xl mb-6">La Chimie de la Peau</h3>
                <p className="text-sm font-light leading-relaxed opacity-80">
                  Le parfum réagit selon votre <span className="font-bold text-brand-gold italic">pH</span>, votre <span className="font-bold text-brand-gold italic">type de peau</span> (sèche ou grasse), ou même votre alimentation. Chaque corps est unique.
                </p>
              </div>
            </div>

            {/* Colonne 2: Pulsation */}
            <div className="bg-brand-black text-brand-offwhite p-10 rounded-[2rem] space-y-8 relative overflow-hidden group luxury-transition hover:shadow-2xl">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center text-brand-gold mb-6">
                <Wind size={24} />
              </div>
              <div>
                <h3 className="font-serif text-3xl mb-4 italic">"Vaporisez, ne frottez pas."</h3>
                <p className="text-[10px] uppercase tracking-widest font-bold text-brand-gold mb-8">Zones de Pulsation</p>
                <ul className="space-y-4 text-[11px] font-bold tracking-widest uppercase opacity-80">
                  <li className="flex items-center gap-4 border-b border-brand-offwhite/10 pb-2">01. DERRIÈRE LES OREILLES</li>
                  <li className="flex items-center gap-4 border-b border-brand-offwhite/10 pb-2">02. SUR LES POIGNETS</li>
                  <li className="flex items-center gap-4 border-b border-brand-offwhite/10 pb-2">03. CREUX DES COUDES</li>
                  <li className="flex items-center gap-4 pb-2">04. DÉCOLLETÉ</li>
                </ul>
              </div>
            </div>

            {/* Colonne 3: Layering */}
            <div className="bg-brand-black text-brand-offwhite p-10 rounded-[2rem] space-y-8 relative overflow-hidden group luxury-transition hover:shadow-2xl">
              <div className="w-12 h-12 bg-brand-gold/20 rounded-full flex items-center justify-center text-brand-gold mb-6">
                <Layers size={24} />
              </div>
              <div>
                <h3 className="font-serif text-3xl mb-6">L'Art du Layering</h3>
                <div className="space-y-6">
                  <div className="flex gap-6 items-start border-b border-brand-offwhite/10 pb-4">
                     <span className="font-serif text-2xl text-brand-gold italic">01</span>
                     <p className="text-[10px] uppercase font-bold tracking-[0.2em] mt-2 opacity-80">CRÈME RICHE OU HUILE</p>
                  </div>
                  <div className="flex gap-6 items-start border-b border-brand-offwhite/10 pb-4">
                     <span className="font-serif text-2xl text-brand-gold italic">02</span>
                     <p className="text-[10px] uppercase font-bold tracking-[0.2em] mt-2 opacity-80">BODY MIST BBW</p>
                  </div>
                  <div className="flex gap-6 items-start">
                     <span className="font-serif text-2xl text-brand-gold italic">03</span>
                     <p className="text-[10px] uppercase font-bold tracking-[0.2em] mt-2 opacity-80">VOTRE PARFUM SIGNATURE</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <DupesSection dupes={dupes} />

      {/* Reste des sections... */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="font-serif text-4xl md:text-5xl mb-4 text-brand-black">Le Rituel du Moment</h2>
            <p className="text-brand-gold font-bold text-[10px] uppercase tracking-widest">Inspiré par vos envies</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16">
            {products.slice(0, 3).map((product) => (
              <ProductCard key={product.id} product={product} onAddToCart={addToCart} />
            ))}
          </div>
        </div>
      </section>
      
      <SocialProof />
    </div>
  );
};

export default Home;
